"use client";

import Link from "next/link";
import { Suspense, useCallback, useEffect, useId, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { BarChart3, Check, ChevronLeft, ChevronRight, ShieldCheck, Target, X, type LucideIcon } from "lucide-react";
import {
  ANALYTICS_CONSENT_EVENT,
  COOKIE_CONSENT_SETTINGS_EVENT,
  type AnalyticsConsent,
  type CookieConsentPreferences,
  readAnalyticsConsent,
  readCookieConsentPreferences,
  writeAnalyticsConsent,
  writeCookieConsentPreferences,
} from "@/lib/analytics-consent";
import {
  captureAnalyticsEvent,
  isInternalAnalyticsViewer,
  setAnalyticsViewerContext,
  trackFavoriteTeamSelect,
  trackLogin,
  trackRegister,
  trackThreadCreate,
} from "@/lib/analytics";

const AUTH_EVENT_COOKIE = "laszti_analytics_auth_event";
const THREAD_CREATE_EVENT_COOKIE = "laszti_analytics_thread_create";
const FAVORITE_TEAM_EVENT_COOKIE = "laszti_analytics_favorite_team";

type AnalyticsViewerStatus = "loading" | "external" | "internal";
type AnalyticsViewerState = {
  pathname: string | null;
  status: AnalyticsViewerStatus;
};

function isAdminRoute(pathname: string | null) {
  return pathname === "/admin" || Boolean(pathname?.startsWith("/admin/"));
}

function readCookie(name: string) {
  const prefix = `${name}=`;
  return document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix))
    ?.slice(prefix.length);
}

function clearCookie(name: string) {
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

function readJsonCookie(name: string) {
  const value = readCookie(name);
  if (!value) return null;
  try {
    return JSON.parse(decodeURIComponent(value)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function authPayloadFromCookie(value: string) {
  const decoded = decodeURIComponent(value);
  if (decoded.startsWith("{")) {
    try {
      const parsed = JSON.parse(decoded) as Record<string, unknown>;
      return {
        event: String(parsed.event ?? ""),
        role: typeof parsed.role === "string" ? parsed.role : null,
        hasUsername: parsed.hasUsername === true,
      };
    } catch {
      return { event: value, role: null, hasUsername: false };
    }
  }
  return { event: decoded, role: null, hasUsername: false };
}

function AnalyticsPageViewTracker() {
  const pathname = usePathname();
  const isAdminPath = isAdminRoute(pathname);
  const previousUrlRef = useRef<string | null>(null);
  const [consent, setConsent] = useState<AnalyticsConsent | null>(() => readAnalyticsConsent());
  const [viewerState, setViewerState] = useState<AnalyticsViewerState>(() => ({
    pathname,
    status: isAdminPath ? "internal" : "loading",
  }));
  const effectiveViewerStatus: AnalyticsViewerStatus = isAdminPath
    ? "internal"
    : viewerState.pathname === pathname
      ? viewerState.status
      : "loading";

  useEffect(() => {
    const onConsentChange = (event: Event) => {
      const nextConsent =
        event instanceof CustomEvent && (event.detail === "accepted" || event.detail === "essential")
          ? event.detail
          : readAnalyticsConsent();
      setConsent(nextConsent);
    };

    window.addEventListener(ANALYTICS_CONSENT_EVENT, onConsentChange);
    return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, onConsentChange);
  }, []);

  useEffect(() => {
    if (isAdminPath) {
      setAnalyticsViewerContext({ isInternal: true });
      return;
    }

    const controller = new AbortController();
    async function loadAnalyticsViewer() {
      try {
        const response = await fetch("/api/analytics-viewer", {
          cache: "no-store",
          headers: { accept: "application/json" },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error("Analytics viewer request failed");
        const payload = (await response.json()) as { isInternal?: unknown };
        const isInternal = payload.isInternal === true;
        setAnalyticsViewerContext({ isInternal });
        setViewerState({ pathname, status: isInternal ? "internal" : "external" });
      } catch {
        if (!controller.signal.aborted) {
          setAnalyticsViewerContext({ isInternal: true });
          setViewerState({ pathname, status: "internal" });
        }
      }
    }

    void loadAnalyticsViewer();
    return () => controller.abort();
  }, [isAdminPath, pathname]);

  useEffect(() => {
    if (!pathname || consent !== "accepted" || effectiveViewerStatus !== "external") return;
    if (previousUrlRef.current === pathname) return;

    previousUrlRef.current = pathname;
    captureAnalyticsEvent("page_view", {
      page_path: pathname,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, consent, effectiveViewerStatus]);

  useEffect(() => {
    if (consent !== "accepted" || effectiveViewerStatus === "loading") return;

    if (effectiveViewerStatus === "internal" || isInternalAnalyticsViewer()) {
      clearCookie(AUTH_EVENT_COOKIE);
      clearCookie(THREAD_CREATE_EVENT_COOKIE);
      clearCookie(FAVORITE_TEAM_EVENT_COOKIE);
      return;
    }

    const authEvent = readCookie(AUTH_EVENT_COOKIE);
    if (authEvent) {
      clearCookie(AUTH_EVENT_COOKIE);
      const payload = authPayloadFromCookie(authEvent);
      if (payload.event === "register" || payload.event === "email_register") {
        trackRegister({ method: "email", hasUsername: payload.hasUsername, role: payload.role });
      } else if (payload.event === "login" || payload.event === "email_login") {
        trackLogin({ method: "email", hasUsername: payload.hasUsername, role: payload.role });
      } else if (payload.event === "google_register") {
        trackRegister({ method: "google", hasUsername: payload.hasUsername, role: payload.role });
      } else if (payload.event === "google_login") {
        trackLogin({ method: "google", hasUsername: payload.hasUsername, role: payload.role });
      }
    }

    const threadCreateEvent = readJsonCookie(THREAD_CREATE_EVENT_COOKIE);
    if (threadCreateEvent) {
      clearCookie(THREAD_CREATE_EVENT_COOKIE);
      if (
        typeof threadCreateEvent.threadId === "string" &&
        typeof threadCreateEvent.categorySlug === "string" &&
        typeof threadCreateEvent.isMatchThread === "boolean"
      ) {
        trackThreadCreate({
          threadId: threadCreateEvent.threadId,
          categorySlug: threadCreateEvent.categorySlug,
          isMatchThread: threadCreateEvent.isMatchThread,
        });
      }
    }

    const favoriteTeamEvent = readJsonCookie(FAVORITE_TEAM_EVENT_COOKIE);
    if (favoriteTeamEvent) {
      clearCookie(FAVORITE_TEAM_EVENT_COOKIE);
      if (typeof favoriteTeamEvent.teamSlug === "string" && typeof favoriteTeamEvent.teamName === "string") {
        trackFavoriteTeamSelect({
          teamSlug: favoriteTeamEvent.teamSlug,
          teamName: favoriteTeamEvent.teamName,
        });
      }
    }
  }, [pathname, consent, effectiveViewerStatus]);

  return null;
}

const consentCategories: Array<{
  id: "necessary" | "analytics" | "marketing";
  title: string;
  description: string;
  icon: LucideIcon;
  locked?: boolean;
}> = [
  {
    id: "necessary",
    title: "Szükséges sütik",
    description: "Ezek elengedhetetlenek az oldal alapvető működéséhez.",
    icon: ShieldCheck,
    locked: true,
  },
  {
    id: "analytics",
    title: "Statisztikai sütik",
    description: "Segítenek megérteni, hogyan használják az oldalt, hogy javíthassuk a teljesítményt.",
    icon: BarChart3,
  },
  {
    id: "marketing",
    title: "Marketing sütik",
    description: "Személyre szabott tartalmak és hirdetések megjelenítését teszik lehetővé.",
    icon: Target,
  },
];

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function ConsentToggle({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange?: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={[
        "relative h-7 w-12 shrink-0 rounded-full border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B] focus-visible:ring-offset-2",
        checked ? "border-[#A4243B] bg-[#A4243B]" : "border-[#D7D2CA] bg-[#F3F0EA]",
        disabled ? "cursor-not-allowed opacity-80" : "hover:border-[#A4243B]/60",
      ].join(" ")}
    >
      <span
        className={[
          "absolute top-1/2 h-5 w-5 -translate-y-1/2 rounded-full bg-white shadow-sm transition",
          checked ? "left-6" : "left-1",
        ].join(" ")}
      >
        {checked ? <Check className="m-1 h-3 w-3 text-[#A4243B]" aria-hidden="true" /> : null}
      </span>
    </button>
  );
}

function CookieCategoryRow({
  category,
  preferences,
  onToggle,
}: {
  category: (typeof consentCategories)[number];
  preferences: CookieConsentPreferences;
  onToggle: (category: "analytics" | "marketing", checked: boolean) => void;
}) {
  const Icon = category.icon;
  const checked = category.id === "necessary" ? true : preferences[category.id];
  let onToggleChange: ((checked: boolean) => void) | undefined;
  if (category.id === "analytics") {
    onToggleChange = (nextChecked) => onToggle("analytics", nextChecked);
  } else if (category.id === "marketing") {
    onToggleChange = (nextChecked) => onToggle("marketing", nextChecked);
  }

  return (
    <div className="flex gap-3 border-t border-[#ECE7DE] py-4 first:border-t-0">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E4DDD2] bg-[#F8F6F2] text-[#A4243B]">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-black uppercase tracking-[0.08em] text-[#111111]">{category.title}</h3>
            <p className="mt-1 text-sm font-medium leading-6 text-[#625B52]">{category.description}</p>
          </div>
          <ConsentToggle
            checked={checked}
            disabled={category.locked}
            label={`${category.title} ${checked ? "bekapcsolva" : "kikapcsolva"}`}
            onChange={onToggleChange}
          />
        </div>
      </div>
    </div>
  );
}

function AnalyticsConsentBanner() {
  const pathname = usePathname();
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const [consent, setConsent] = useState<AnalyticsConsent | null>(() => readAnalyticsConsent());
  const [hasLoadedConsent] = useState(() => typeof window !== "undefined");
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [view, setView] = useState<"summary" | "settings">("summary");
  const [preferences, setPreferences] = useState<CookieConsentPreferences>(() => readCookieConsentPreferences());

  const choose = useCallback((nextConsent: AnalyticsConsent) => {
    writeAnalyticsConsent(nextConsent);
    setConsent(nextConsent);
    setIsSettingsOpen(false);
  }, []);

  const dismiss = useCallback(() => {
    const storedConsent = readAnalyticsConsent();
    if (storedConsent) {
      setConsent(storedConsent);
      setIsSettingsOpen(false);
      return;
    }

    choose("essential");
  }, [choose]);

  useEffect(() => {
    const onConsentChange = () => {
      setConsent(readAnalyticsConsent());
      setPreferences(readCookieConsentPreferences());
    };
    const onSettingsOpen = () => {
      setConsent(readAnalyticsConsent());
      setPreferences(readCookieConsentPreferences());
      setView("settings");
      setIsSettingsOpen(true);
    };
    window.addEventListener(ANALYTICS_CONSENT_EVENT, onConsentChange);
    window.addEventListener(COOKIE_CONSENT_SETTINGS_EVENT, onSettingsOpen);
    return () => {
      window.removeEventListener(ANALYTICS_CONSENT_EVENT, onConsentChange);
      window.removeEventListener(COOKIE_CONSENT_SETTINGS_EVENT, onSettingsOpen);
    };
  }, []);

  useEffect(() => {
    const isOpen = !consent || isSettingsOpen;
    if (!hasLoadedConsent || !isOpen || isAdminRoute(pathname)) return;
    previousActiveElementRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    dialogRef.current?.querySelector<HTMLElement>(focusableSelector)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dismiss();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusableElements = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        (element) => element.offsetParent !== null,
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (!firstElement || !lastElement) return;
      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      } else if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
      previousActiveElementRef.current?.focus();
    };
  }, [consent, dismiss, hasLoadedConsent, isSettingsOpen, pathname]);

  const isOpen = !consent || isSettingsOpen;
  if (!hasLoadedConsent || !isOpen || isAdminRoute(pathname)) return null;

  function saveSettings() {
    writeCookieConsentPreferences(preferences);
    setConsent(preferences.analytics ? "accepted" : "essential");
    setIsSettingsOpen(false);
  }

  function togglePreference(category: "analytics" | "marketing", checked: boolean) {
    setPreferences((current) => ({ ...current, [category]: checked }));
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-end justify-center px-0 sm:items-center sm:px-5">
      <div
        aria-hidden="true"
        className="absolute inset-0 z-0 cursor-default bg-black/70 backdrop-blur-lg"
        onClick={dismiss}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="cookie-consent-modal-enter relative z-10 max-h-[92svh] w-full overflow-y-auto rounded-t-3xl border border-white/70 bg-[#FFFDF9] text-[#111111] shadow-[0_30px_90px_rgba(0,0,0,0.55)] outline-none sm:max-w-[680px] sm:rounded-3xl"
      >
        <div className="border-b border-[#ECE7DE] px-5 pb-4 pt-5 sm:px-8 sm:pt-7">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#A4243B]">Adatvédelmi beállítás</p>
              <h2 id={titleId} className="mt-2 text-2xl font-black leading-tight text-[#0F0F0F] sm:text-3xl">
                Sütiket használunk
              </h2>
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Cookie beállítások bezárása"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#E4DDD2] bg-white text-[#111111] shadow-sm transition hover:border-[#A4243B]/50 hover:text-[#A4243B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B] focus-visible:ring-offset-2"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="px-5 py-5 sm:px-8 sm:py-7">
          {view === "settings" ? (
            <button
              type="button"
              onClick={() => setView("summary")}
              className="mb-4 inline-flex items-center gap-1 text-xs font-black uppercase tracking-[0.16em] text-[#A4243B] transition hover:text-[#78182A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B] focus-visible:ring-offset-2"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              Vissza
            </button>
          ) : null}

          <p id={descriptionId} className="text-sm font-medium leading-7 text-[#4F473E] sm:text-base">
            Az oldal működéséhez szükséges sütiket mindig használjuk. A statisztikai és marketing célú sütikhez az Ön
            előzetes hozzájárulása szükséges. A részleteket a{" "}
            <Link
              href="/privacy#sutik-es-analitika"
              className="font-black text-[#A4243B] underline decoration-[#A4243B]/30 underline-offset-4 transition hover:text-[#78182A]"
            >
              cookie tájékoztatóban
            </Link>{" "}
            találja.
          </p>

          {view === "settings" ? (
            <div className="mt-6 rounded-2xl border border-[#ECE7DE] bg-white px-4">
              {consentCategories.map((category) => (
                <CookieCategoryRow
                  key={category.id}
                  category={category}
                  preferences={preferences}
                  onToggle={togglePreference}
                />
              ))}
            </div>
          ) : (
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              {consentCategories.map((category) => {
                const Icon = category.icon;
                return (
                  <div key={category.id} className="rounded-2xl border border-[#ECE7DE] bg-white p-4">
                    <Icon className="h-5 w-5 text-[#A4243B]" aria-hidden="true" />
                    <h3 className="mt-3 text-xs font-black uppercase tracking-[0.12em] text-[#111111]">
                      {category.title}
                    </h3>
                    <p className="mt-2 text-xs font-semibold leading-5 text-[#6B6256]">{category.description}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-[#ECE7DE] bg-white px-5 py-4 sm:px-8 sm:py-5">
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => choose("essential")}
              className="h-12 rounded-xl border border-[#D7D2CA] bg-white px-5 text-sm font-black uppercase tracking-[0.06em] text-[#111111] transition hover:border-[#A4243B]/50 hover:text-[#A4243B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B] focus-visible:ring-offset-2"
            >
              Csak szükséges
            </button>
            <div className="flex flex-col gap-3 sm:flex-row">
              {view === "summary" ? (
                <button
                  type="button"
                  onClick={() => setView("settings")}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-xl px-4 text-sm font-black uppercase tracking-[0.06em] text-[#A4243B] transition hover:bg-[#F8F1F2] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B] focus-visible:ring-offset-2"
                >
                  Beállítások
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={saveSettings}
                  className="h-12 rounded-xl border border-[#D7D2CA] bg-white px-5 text-sm font-black uppercase tracking-[0.06em] text-[#111111] transition hover:border-[#A4243B]/50 hover:text-[#A4243B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B] focus-visible:ring-offset-2"
                >
                  Beállítások mentése
                </button>
              )}
              <button
                type="button"
                onClick={() => choose("accepted")}
                className="h-12 rounded-xl bg-[#A4243B] px-5 text-sm font-black uppercase tracking-[0.06em] text-white shadow-[0_12px_26px_rgba(164,36,59,0.28)] transition hover:bg-[#8E1F33] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B] focus-visible:ring-offset-2"
              >
                Elfogadom az összeset
              </button>
            </div>
          </div>
          <p className="mt-4 text-xs font-semibold leading-5 text-[#6B6256]">
            Bármikor módosíthatja a beállításait az{" "}
            <Link href="/privacy" className="font-black text-[#A4243B] hover:text-[#78182A]">
              Adatvédelem
            </Link>{" "}
            oldalon.
          </p>
        </div>
      </div>
    </div>
  );
}

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Suspense fallback={null}>
        <AnalyticsPageViewTracker />
      </Suspense>
      <AnalyticsConsentBanner />
      {children}
    </>
  );
}
